import { useEffect, useMemo, useState } from "react";

const CONVERSATION_REPORT_BASE =
  "https://educationforall.app.n8n.cloud/webhook/conversation-history-report";

type ConversationMessage = {
  id?: string;
  ts?: string;
  session_id?: string;
  sender?: string;
  message?: string | null;
  activity_family?: string | null;
  activity_type?: string | null;
  activity_event?: string | null;
  user_input_mode?: string | null;
  assistant_output_mode?: string | null;
  reassurance_detected?: boolean | null;
  safety_event_detected?: boolean | null;
  event_type?: string | null;
};

type ConversationReportPayload = {
  sessionId?: string;
  historyAnchor?: string;
  historyEndAnchor?: string | null;
  messages?: ConversationMessage[];
  count?: number;
  error?: string;
  message?: string;
};

function buildReportUrl(
  sessionId: string,
  historyAnchor: string,
  historyEndAnchor?: string
): string {
  const u = new URL(CONVERSATION_REPORT_BASE);
  u.searchParams.set("sessionId", sessionId);
  u.searchParams.set("historyAnchor", historyAnchor);

  if (historyEndAnchor) {
    u.searchParams.set("historyEndAnchor", historyEndAnchor);
  }

  return u.toString();
}

function formatSender(sender?: string): string {
  if (!sender) return "Unknown";
  if (sender === "user") return "User";
  if (sender === "assistant") return "Assistant";
  return sender.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTimestamp(ts?: string): string {
  if (!ts) return "";

  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;

  return d.toLocaleString([], {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function csvEscape(value: unknown): string {
  const s = String(value ?? "");
  const escaped = s.replace(/"/g, '""');

  return /[",\n\r]/.test(s) ? `"${escaped}"` : escaped;
}

export default function ConversationHistoryReportPage() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const sessionId = (params.get("sessionId") ?? "").trim();
  const historyAnchor = (params.get("historyAnchor") ?? "").trim();
  const historyEndAnchor = (params.get("historyEndAnchor") ?? "").trim();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<ConversationReportPayload | null>(null);

  function savePdf() {
    const previousTitle = document.title;

    const safeSessionId = sessionId.replace(/[^a-z0-9_-]/gi, "_");
    document.title = `conversation-history-${safeSessionId}`;

    window.print();

    window.setTimeout(() => {
      document.title = previousTitle;
    }, 500);
  }

  function exportCsv() {
    const headers = ["Timestamp", "Sender", "Message"];

    const rows = messages.map((item) => [
      formatTimestamp(item.ts),
      formatSender(item.sender).toLowerCase(),
      item.message ?? "",
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");

    const blob = new Blob([`\uFEFF${csv}`], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    const safeSessionId = sessionId.replace(/[^a-z0-9_-]/gi, "_");
    const exportedAt = new Date().toISOString().slice(0, 10);

    a.href = url;
    a.download = `conversation-history-${safeSessionId}-${exportedAt}.csv`;

    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  }

  useEffect(() => {
    if (!sessionId || !historyAnchor) {
      setPayload(null);
      setError(null);
      return;
    }

    let cancelled = false;
    const url = buildReportUrl(sessionId, historyAnchor, historyEndAnchor);

    (async () => {
      setLoading(true);
      setError(null);
      setPayload(null);

      try {
        const res = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });

        const text = await res.text();

        let data: unknown = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          throw new Error("Response was not valid JSON");
        }

        if (cancelled) return;

        if (!res.ok) {
          const msg =
            typeof data === "object" &&
              data !== null &&
              "message" in data &&
              typeof (data as { message?: unknown }).message === "string"
              ? String((data as { message?: unknown }).message)
              : undefined;

          throw new Error(msg || `Request failed (${res.status})`);
        }

        const root =
          Array.isArray(data) && data.length > 0 && typeof data[0] === "object"
            ? (data[0] as ConversationReportPayload)
            : (data as ConversationReportPayload);

        setPayload(root);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, historyAnchor, historyEndAnchor]);

  const messages = payload?.messages ?? [];

  return (
    <div className="h-screen overflow-y-auto bg-slate-50 text-slate-900 print:h-auto print:overflow-visible">
      <div className="mx-auto max-w-5xl px-4 py-10 print:max-w-none print:px-0 print:py-0">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Conversation Log
            </h1>

            <p className="mt-2 text-sm text-slate-600">
              Conversation transcript for this homework activity
            </p>
          </div>

          {messages.length > 0 && (
            <div className="flex flex-wrap gap-2 print:hidden">
              <button
                type="button"
                onClick={exportCsv}
                className="rounded-full bg-white px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 hover:text-slate-950"
              >
                Export CSV
              </button>

              <button
                type="button"
                onClick={savePdf}
                className="rounded-full bg-white px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 hover:text-slate-950"
              >
                Save PDF
              </button>
            </div>
          )}
        </div>

        {(!sessionId || !historyAnchor) && (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Conversation report is not available yet. This report becomes available after the homework activity is completed.
          </div>
        )}

        {sessionId && historyAnchor && (
          <dl className="mt-6 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase text-slate-500">
                Session ID
              </dt>
              <dd className="mt-0.5 break-all font-medium text-slate-900">
                {sessionId}
              </dd>
            </div>

            <div>
              <dt className="text-xs font-medium uppercase text-slate-500">
                History anchor
              </dt>
              <dd className="mt-0.5 break-all text-slate-800">
                {historyAnchor}
              </dd>
            </div>

            {historyEndAnchor && (
              <div>
                <dt className="text-xs font-medium uppercase text-slate-500">
                  History end anchor
                </dt>
                <dd className="mt-0.5 break-all text-slate-800">
                  {historyEndAnchor}
                </dd>
              </div>
            )}

            <div>
              <dt className="text-xs font-medium uppercase text-slate-500">
                Messages
              </dt>
              <dd className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">
                {messages.length}
              </dd>
            </div>
          </dl>
        )}

        {loading && (
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-600">
            <span
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"
              aria-hidden
            />
            Loading conversation history…
          </div>
        )}

        {error && (
          <div
            className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
            role="alert"
          >
            {error}
          </div>
        )}

        {!loading && !error && sessionId && historyAnchor && messages.length === 0 && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
            No conversation messages found for this session and history anchor.
          </div>
        )}

        {messages.length > 0 && (
          <div className="mt-8 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="w-52 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Timestamp
                  </th>
                  <th className="w-32 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Sender
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Message
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 bg-white">
                {messages.map((item, index) => (
                  <tr key={`${item.ts ?? "message"}-${index}`}>
                    <td className="whitespace-nowrap px-4 py-5 align-top text-slate-700">
                      {formatTimestamp(item.ts)}
                    </td>

                    <td className="whitespace-nowrap px-4 py-5 align-top font-medium text-slate-900">
                      {formatSender(item.sender).toLowerCase()}
                    </td>

                    <td className="px-4 py-5 align-top text-slate-800">
                      <div className="whitespace-pre-wrap leading-6">
                        {item.message || "[No message text]"}
                      </div>

                      {(item.activity_event ||
                        item.activity_family ||
                        item.activity_type ||
                        item.user_input_mode ||
                        item.assistant_output_mode ||
                        item.reassurance_detected ||
                        item.safety_event_detected ||
                        item.event_type) && (
                          <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500 print:hidden">
                            {item.activity_family && <span>{item.activity_family}</span>}
                            {item.activity_type && <span>{item.activity_type}</span>}
                            {item.activity_event && <span>{item.activity_event}</span>}
                            {item.user_input_mode && (
                              <span>User input: {item.user_input_mode}</span>
                            )}
                            {item.assistant_output_mode && (
                              <span>Assistant output: {item.assistant_output_mode}</span>
                            )}
                            {item.reassurance_detected && (
                              <span className="font-medium text-amber-700">
                                Reassurance detected
                              </span>
                            )}
                            {item.safety_event_detected && (
                              <span className="font-medium text-red-700">
                                Safety event detected
                              </span>
                            )}
                            {item.event_type && <span>Event type: {item.event_type}</span>}
                          </div>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}